/*
 *   File : family-details.js
 *   Author : https://evoqins.com
 *   Description : Family detail tab file
 *   Integrations : 'react-toastify'
 *   Version : 1.0.0
*/

// custom packages
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Modal } from 'bootstrap';
import { toast } from 'react-toastify';

// custom components
import { CustomDataTable } from '../../../Components/DataTable';
import { Chip } from '../../../Components/Others';
import { PrimaryButton, TextButton } from '../../../Components/Buttons';
import { AddFamilyModal, UnlinkAccountModal } from '../../../Components/Modal';
import { APIService } from '../../../Service';
import { useNavigate, useParams } from 'react-router-dom';
import EmptyScreen from '../../../Components/Others/empty-screen';


const FamilyDetails = () => {

    const isRendered = useRef(true);
    const { id } = useParams();

    const navigate = useNavigate();

    const [currentPage, setCurrentPage] = useState(1);
    const [loader, setLoader] = useState(true);
    const [addFamilyModal, setAddFamilyModal] = useState(false);
    const [unlinkModal, setUnlinkModal] = useState(false);
    const [familyDetails, setFamilyDetails] = useState({});
    const [isPaginationChanged, setIsPaginationChanged] = useState(false);

    useEffect(() => {
        if (isRendered.current === true) {
            _familyDetails();
        }
        isRendered.current = false;
    }, [])

    useEffect(() => {
        if (addFamilyModal === true) {
            const open_modal = new Modal(document.getElementById('add-family-modal'));
            open_modal.show();
        }
    }, [addFamilyModal]);

    useEffect(() => {
        if (unlinkModal === true) {
            const open_modal = new Modal(document.getElementById('unlink-modal'));
            open_modal.show();
        }
    }, [unlinkModal]);

    // handle table page change 
    const _handleTablePageChange = (page_num) => {

        _familyDetails(page_num)
        setCurrentPage(page_num);
    };

    // handle add family 
    const _handleAddFamily = () => {
        setAddFamilyModal(true);
    };

    // handle unlink  account 
    const _handleUnlink = () => {
        setUnlinkModal(true);
    };

    // Pending request data table column 
    const FAMILY_TABLE_COLUMN = [
        {
            name: 'Customer ID',
            selector: row => row.customer_id,
            width: "150px"
        },
        {
            name: 'Name',
            selector: row => row.name,
            width: "240px",
            sortable: true
        },
        {
            name: 'Individual AUM',
            selector: row => row.individual_aum,
            width: "160px"
        },
        {
            name: 'Status',
            cell: (row) =>
                <Chip label={row.status}
                    className={row.status === "Active" ? "e-bg-green-crayola" : "e-bg-taupe-gray"} />,
            width: "140px"
        },
        // {
        //     name: "",
        //     cell: () =>
        //         <TextButton name="Unlink"
        //             onClick={_handleUnlink}
        //             className='e-hover' />
        // }
    ];

    const _familyDetails = (page_num = currentPage) => {
        const is_post = true;
        const url = 'user/family';
        const request = {
            "customer_id": id,
            "page_num": page_num,
            "page_size": 10
        }
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                setFamilyDetails(response.data);
            }
            else {
                setFamilyDetails({ family_details: [] });
            }
            setLoader(false);
        })
    };

    return (
        <Fragment>
            {loader ?
                <div className='d-flex align-items-center justify-content-center py-5 min-vh-50'>
                    <img src={require("../../../Assets/Image/loader.gif")}
                        draggable={false}
                        alt='loader'
                        width={80}
                        height={80}
                        className='text-center' />
                </div>
                :
                <>
                    {familyDetails?.family_details?.length > 0 &&
                        <div className='padding-32px-top padding-32px-lr'>
                            {/* Family head */}
                            <h6 className='mb-3 e-text-smoky-black e-primary-font e-font-16 e-font-weight-600 line-height-20px'>
                                Family head
                            </h6>

                            <div className='row'>
                                <div className='col-6'>
                                    <div className={`d-flex gap-12px align-items-center e-bg-lavender-dark border-radius-4 p-3`}>
                                        <div className='d-flex flex-column h-100 justify-content-center gap-2 pe-4'>
                                            <p className='mb-0 e-text-dark-silver e-primary-font e-font-14 e-font-weight-400 line-height-16px '>
                                                Customer ID
                                            </p>
                                            <p className='mb-0 e-text-chinese-black e-primary-font e-font-14 e-font-weight-600 line-height-20px'>
                                                {familyDetails.family_head.customer_id}
                                            </p>
                                        </div>

                                        <div className='vr e-bg-vodka-light'></div>
                                        <div className='d-flex flex-column h-100 justify-content-center gap-2 pe-4'>
                                            <p className='mb-0 e-text-dark-silver e-primary-font e-font-14 e-font-weight-400 line-height-16px '>
                                                Name
                                            </p>
                                            <p className='mb-0 e-text-chinese-black e-primary-font e-font-14 e-font-weight-600 line-height-20px'>
                                                {familyDetails.family_head.name}
                                            </p>
                                        </div>
                                        <div className='vr e-bg-vodka-light'></div>
                                        <div className='d-flex flex-column h-100 justify-content-center gap-2 pe-4'>
                                            <p className='mb-0 e-text-dark-silver e-primary-font e-font-14 e-font-weight-400 line-height-16px '>
                                                AUM
                                            </p>
                                            <p className='mb-0 e-text-chinese-black e-primary-font e-font-14 e-font-weight-600 line-height-20px'>
                                                {familyDetails.family_head.individual_aum}
                                            </p>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                    }

                    {/* Family members list  */}
                    <div className='pt-3 mt-1'>
                        {familyDetails?.family_details?.length !== 0 ? (
                            <>
                                <div className='d-flex align-items-center justify-content-between padding-32px-lr'>
                                    <h6 className='mb-0 e-text-smoky-black e-primary-font e-font-16 e-font-weight-600 line-height-20px'>
                                        Family members list
                                    </h6>
                                    {/* <PrimaryButton name="Add Family Member"
                                    paddingBlock="padding-12px-tb px-3"
                                    onClick={_handleAddFamily} /> */}
                                </div>

                                {/* Family members list table */}
                                <div className='e-data-table-wrapper-default-updated pt-3 pb-4'>
                                    <CustomDataTable columns={FAMILY_TABLE_COLUMN}
                                        data={familyDetails.family_details}
                                        pagination={true}
                                        paginationPerPage={10}
                                        paginationTotalRows={familyDetails.total_family_records}
                                        paginationDefaultPage={familyDetails.current_page}
                                        lastPage={familyDetails.total_page}
                                        onChangePage={_handleTablePageChange} />
                                </div>
                            </>
                        )
                            : (
                                <EmptyScreen
                                    image={require('../../../Assets/Image/NotFound/no-data.png')}
                                    title='No results found'
                                    description={`We couldn' t find anything that matches your search. Try checking your spelling, using different keywords, or exploring popular funds.`}
                                />
                            )

                        }

                    </div>
                </>
            }


            {/* Add family modal */}
            {addFamilyModal === true &&
                <AddFamilyModal
                    handleClose={() => {
                        setTimeout(() => {
                            setAddFamilyModal(false);
                        }, 200);
                    }} />
            }

            {/* Unlink account modal */}
            {unlinkModal === true &&
                <UnlinkAccountModal
                    handleClose={() => {
                        setTimeout(() => {
                            setUnlinkModal(false);
                        }, 200);
                    }}
                    handleSuccess={() => {
                        setTimeout(() => {
                            setUnlinkModal(false);
                        }, 200);
                    }} />
            }
        </Fragment>
    )
}

export default FamilyDetails;
